import { snakeToCamel } from '@utils';

/**
 * Build URL with query parameters from payload object
 * @param {string} baseUrl - Base URL
 * @param {object} payload - Query parameters object
 * @returns {string} - Complete URL with query string
 */
export const buildUrlWithParams = (baseUrl, payload = {}) => {
  const queryParams = new URLSearchParams();

  // Add all non-null, non-undefined values to query params
  Object.entries(payload).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      queryParams.append(key, value);
    }
  });

  const queryString = queryParams.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};

/**
 * Build URL with query parameters for ticket search
 * Transforms frontend payload to backend query parameters
 * @param {string} baseUrl - Base URL
 * @param {object} payload - Search payload object
 * @param {string} payload.searchKey - Search key (field name)
 * @param {string} payload.searchValue - Search value
 * @param {number} payload.pageNumber - Page number (0-indexed)
 * @param {number} payload.pageSize - Page size
 * @param {object} payload.filters - Filter object with status array
 * @param {string[]} payload.filters.status - Array of status values
 * @param {string} payload.rangeKey - Range key (e.g., 'created_at', 'updated_at')
 * @param {object} payload.rangeValue - Range value object
 * @param {string} payload.rangeValue.min - Minimum date (ISO string)
 * @param {string} payload.rangeValue.max - Maximum date (ISO string)
 * @returns {string} - Complete URL with query string
 */
export const buildTicketSearchUrl = (baseUrl, payload = {}) => {
  const queryParams = new URLSearchParams();

  // Search parameters
  if (
    payload.searchKey?.toString().trim() &&
    payload.searchValue?.toString().trim()
  ) {
    queryParams.append(payload.searchKey, payload.searchValue);
  }

  // Map pageNumber to page
  if (payload.pageNumber !== null && payload.pageNumber !== undefined) {
    queryParams.append('page', payload.pageNumber);
  }

  if (payload.pageSize !== null && payload.pageSize !== undefined) {
    queryParams.append('pageSize', payload.pageSize);
  }

  // Handle status filter as comma-separated string
  if (
    payload.filters?.status &&
    Array.isArray(payload.filters.status) &&
    payload.filters.status.length > 0
  ) {
    queryParams.append('status', payload.filters.status.join(','));
  }

  // Handle date range - convert rangeKey from snake_case to camelCase and use .from/.to format
  if (payload.rangeKey && payload.rangeValue) {
    const dateField = snakeToCamel(payload.rangeKey);

    if (payload.rangeValue.min) {
      queryParams.append(`${dateField}From`, payload.rangeValue.min);
    }

    if (payload.rangeValue.max) {
      queryParams.append(`${dateField}To`, payload.rangeValue.max);
    }
  }

  const queryString = queryParams.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};

/**
 * Build query string from payload object
 * @param {object} payload - Query parameters object
 * @returns {string} - Query string (without leading '?')
 */
export const buildQueryString = (payload = {}) => {
  const queryParams = new URLSearchParams();

  Object.entries(payload).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      queryParams.append(key, value);
    }
  });

  return queryParams.toString();
};

/**
 * Build URL for admin tickets API with filters, sorting, and pagination
 * @param {string} baseUrl - Base URL
 * @param {object} payload - Query parameters object
 * @param {string} payload.ticketId - Filter by ticket ID
 * @param {string} payload.agentId - Filter by agent ID
 * @param {string|string[]} payload.status - Filter by status (string or array)
 * @param {number} payload.lastHours - Filter by last N hours (for closed tickets)
 * @param {string} payload.sortBy - Sort field (createdAt, updatedAt, etc.)
 * @param {string} payload.sortDir - Sort direction (asc/desc)
 * @param {number} payload.page - Page number (0-indexed)
 * @param {number} payload.pageSize - Page size
 * @returns {string} - Complete URL with query string
 */
export const buildAdminTicketsUrl = (baseUrl, payload = {}) => {
  const queryParams = new URLSearchParams();

  // Filter parameters
  if (payload.ticketId?.toString().trim()) {
    queryParams.append('ticketId', payload.ticketId);
  }

  if (payload.agentId?.toString().trim()) {
    queryParams.append('agentId', payload.agentId);
  }

  // Handle status as comma-separated string
  if (payload.status) {
    const statusValue = Array.isArray(payload.status)
      ? payload.status.join(',')
      : payload.status;
    if (statusValue) {
      queryParams.append('status', statusValue);
    }
  }

  // Last hours filter (for closed tickets)
  if (payload.lastHours !== null && payload.lastHours !== undefined) {
    queryParams.append('lastHours', payload.lastHours);
  }

  // Sorting parameters
  if (payload.sortBy?.toString().trim()) {
    queryParams.append('sortBy', payload.sortBy);
  }

  if (payload.sortDir?.toString().trim()) {
    queryParams.append('sortDir', payload.sortDir);
  }

  // Pagination parameters
  if (payload.page !== null && payload.page !== undefined) {
    queryParams.append('page', payload.page);
  }

  if (payload.pageSize !== null && payload.pageSize !== undefined) {
    queryParams.append('pageSize', payload.pageSize);
  }

  const queryString = queryParams.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};
